import React, { useState, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { WelletContext } from '../../../context/WelletContext';
import useForm from '../../../helpers/useForm';
import { Spinner } from '../../../components/Common/Spinner/Spinner';
import { useAuth } from '../../../auth/AuthContext';
import { getAppTenant } from '../../../services/appService';
import InputForm from '../../../components/Common/InputForm/InputForm';
import './LoginScreen.css';

const LoginPlatformScreen = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const history = useHistory();
  const welletContext = useContext(WelletContext);
  const [errorLogin, setErrorLogin] = useState(undefined);
  const [verifyingUser, setVerifyingUser] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const app = useSelector((state) => state.app);
  const logo = app.isPlatform
    ? app.platformMedia.find((m) => m.type === 'LOGO_TRANSPARENT')?.value
    : app?.media?.find((m) => m.type === 'LOGO_TRANSPARENT')?.value;

  const [errorEmailForgotPassword, setErrorEmailForgotPassword] = useState(undefined);
  const [errorPlatformForgotPassword, setErrorPlatformForgotPassword] = useState(undefined);

  function validate(values) {
    let errors = {};

    if (app.isPlatform && !values.platform) {
      errors.platform = t('login.errorPlatform');
    }

    if (!values.email) {
      errors.email = t('emailError.EMPTY_EMAIL');
    } else if (!/\S+@\S+\.\S+/.test(values.email)) {
      errors.email = t('emailError.INVALID_EMAIL');
    }

    if (!values.password) {
      errors.password = t('login.errorPassword');
    }

    return errors;
  }

  const sendForm = async () => {
    setVerifyingUser(true);

    try {
      if (app.isPlatform) {
        const result = await getAppTenant(values.platform, app.language, welletContext);
        await login(values.email, values.password, result.tenantId, () => {
          history.push('/');
        });
      } else {
        await login(values.email, values.password, app.tenantId, () => history.push('/'));
      }
    } catch (error) {
      switch (error.code) {
        case 'auth/wrong-password':
          setAttempts(attempts + 1);
          setErrorLogin(t('passwordError.PASSWORD_WRONG'));
          break;
        case 'auth/user-disabled':
          setErrorLogin(t('emailError.USER_SUSPENDED'));
          setAttempts(attempts + 1);
          break;
        default:
          setAttempts(attempts + 1);
          setErrorLogin(t('callApiError'));
          break;
      }
      setVerifyingUser(false);
    }
  };

  const { values, errors, handleChange, handleSubmit } = useForm(sendForm, validate);

  const onSignUpHandle = () => {
    history.push({
      pathname: '/sign-up-terms',
    });
  };

  const onForgotPasswordHandle = async () => {
    if (!values.email) {
     setErrorEmailForgotPassword(t('emailError.EMPTY_EMAIL'));
     return;
    } else if (!/\S+@\S+\.\S+/.test(values.email)) {
      setErrorEmailForgotPassword(t('emailError.INVALID_EMAIL'));
      return;
    }

    if(app.isPlatform) {
      if (!values.platform) {
        setErrorPlatformForgotPassword(t('login.errorPlatform'));
        return;
      }
    }

    try {
      let tenantId = app.tenantId;
      if (app.isPlatform) {
        const result = await getAppTenant(values.platform, app.language, welletContext);
        tenantId = result.tenantId;
      }

      history.push({
        pathname: '/forgot-password',
        search: `?email=${values.email}&tenantId=${tenantId}`,
      });
    } catch (error) {
      if (error.response?.data === 'NO_ENTITY_FOUND') {
        setErrorPlatformForgotPassword(t('login.errorPlatformNotFound'));
      } else {
        setErrorPlatformForgotPassword(t('callApiError'));
      }
    }
  };

  return (
    <div className='wl-wrap'>
      <div className='wl-ribbon' aria-hidden='true'></div>
      <header className='wl-head'>
        <img src={logo} alt={`${app?.platformName} logo`} />
        <p className='wl-sub'>{t('login.accessPlatform', { organization: app?.platformName })}</p>
      </header>
      <form onSubmit={handleSubmit}>
        <main className='wl-card' role='form' aria-label='Wellet login'>
          {errorLogin && <div className='wl-error'>{errorLogin}</div>}

          {app?.isPlatform && (
            <InputForm
              name='platform'
              type='text'
              value={values.platform}
              error={errors.platform || errorPlatformForgotPassword}
              label={t('platform')}
              placeholder={t('platform')}
              splitText='wellet.pro'
              onChange={(e) => { setErrorPlatformForgotPassword(undefined); handleChange(e); }}
            />
          )}

          <InputForm
            name={'email'}
            type={'email'}
            value={values.email}
            placeholder={t('enterEmail')}
            label='E-mail'
            error={errors.email || errorEmailForgotPassword}
            onChange={(e) => { setErrorEmailForgotPassword(undefined); handleChange(e); }}
            forceLowecase={true}
          />

          <div style={{ position: 'relative' }}>
            <InputForm
              name={'password'}
              type={'password'}
              label={t('login.password')}
              placeholder={t('enterPassword')}
              value={values.password}
              error={errors.password}
              onChange={handleChange}
            />
            <a
              href='#'
              onClick={(e) => {
                e.preventDefault();
                onForgotPasswordHandle();
              }}
              style={{
                position: 'absolute',
                right: '0',
                top: '2px',
                color: 'var(--brand)',
                textDecoration: 'none',
                fontSize: '14px',
              }}
            >
              {t('login.forgotPassword')}
            </a>
          </div>

          <button className='wl-btn' type='submit'>
            {verifyingUser ? (
              <div className='d-flex justify-content-center'>
                <Spinner size={25} color='#fff' />
              </div>
            ) : (
              t('login.login')
            )}
          </button>

          {app?.currentMarket?.canRegisterConcierges && !app?.isPlatform && (
            <p className='wl-new'>
              {t('login.areNew', { organization: app?.titleWeb })}{' '}
              <a
                href=''
                onClick={(e) => {
                  e.preventDefault();
                  onSignUpHandle();
                }}
                style={{ cursor: 'pointer' }}
              >
                {t('login.signUp')}
              </a>
            </p>
          )}
        </main>
      </form>
    </div>
  );
};

export default LoginPlatformScreen;
